import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

/**
 * 테마 타입 정의
 */
export type Theme = "light" | "dark";

/**
 * UI 상태 인터페이스
 */
interface UIState {
  // 사이드바 상태
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // 테마 설정
  theme: Theme;

  // 로딩 상태
  globalLoading: boolean;
  loadingMessage: string;

  // 모바일 감지
  isMobile: boolean;

  // 액션
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  toggleSidebarCollapse: () => void;

  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  setGlobalLoading: (loading: boolean, message?: string) => void;

  setIsMobile: (isMobile: boolean) => void;
}

/**
 * 슈퍼어드민 UI 상태 관리 Store
 */
export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        // 초기 상태
        sidebarOpen: true,
        sidebarCollapsed: false,
        theme: "light",
        globalLoading: false,
        loadingMessage: "",
        isMobile: false,

        // 사이드바 상태 관리
        setSidebarOpen: (open) => {
          console.log("🔧 uiStore.setSidebarOpen:", open);
          set({ sidebarOpen: open });
        },

        setSidebarCollapsed: (collapsed) => {
          console.log("🔧 uiStore.setSidebarCollapsed:", collapsed);
          set({ sidebarCollapsed: collapsed });
        },

        toggleSidebar: () => {
          const newState = !get().sidebarOpen;
          console.log("🔧 uiStore.toggleSidebar:", newState);
          set({ sidebarOpen: newState });
        },

        toggleSidebarCollapse: () => {
          const newState = !get().sidebarCollapsed;
          console.log("🔧 uiStore.toggleSidebarCollapse:", newState);
          set({ sidebarCollapsed: newState });
        },

        // 테마 관리
        setTheme: (theme) => {
          console.log("🎨 uiStore.setTheme:", theme);
          set({ theme });

          // HTML 요소에 테마 클래스 적용
          if (typeof document !== "undefined") {
            document.documentElement.setAttribute("data-theme", theme);
          }
        },

        toggleTheme: () => {
          const currentTheme = get().theme;
          const newTheme: Theme = currentTheme === "light" ? "dark" : "light";
          console.log("🎨 uiStore.toggleTheme:", currentTheme, "→", newTheme);
          get().setTheme(newTheme);
        },

        // 글로벌 로딩 관리
        setGlobalLoading: (loading, message = "") => {
          console.log("⏳ uiStore.setGlobalLoading:", loading, message);
          set({
            globalLoading: loading,
            loadingMessage: message,
          });
        },

        // 모바일 상태 관리
        setIsMobile: (isMobile) => {
          console.log("📱 uiStore.setIsMobile:", isMobile);
          set({ isMobile });
        },
      }),
      {
        name: "super-admin-ui-store",
        // 일부 상태만 persist (테마, 사이드바 설정)
        partialize: (state) => ({
          theme: state.theme,
          sidebarCollapsed: state.sidebarCollapsed,
        }),
      }
    ),
    {
      name: "super-admin-ui-store",
    }
  )
);
